import React, { CSSProperties, FC, ForwardedRef, forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { debounce, omit } from 'lodash';
import SearchIcon from '../icons/search-solid.svg';
import { Icon } from '../icon';
import theme from '../theme/theme';
import { isMobile, ReactType, T } from '../helpers/helpers';
import { BasicButton } from '../buttons';
import CopyIcon from '../svg/copyIcon';
import EyeSolid from '../svg/eyeSolid';
import LowVisionSolid from '../svg/lowVisionSolid';

interface InputProps {
    fullWidth?: boolean;
    isError?: boolean;
}

export const Input = styled.input.withConfig({
    shouldForwardProp: (prop) => !['fullWidth', 'isError'].includes(prop)
})<InputProps>`
    color: ${(props) => props.theme.input.color};
    border: 1px #ddd solid;
    border-radius: 4px;
    padding: 5px 10px;
    font-family: ${(props) => props.theme.app.font};
    font-size: ${(props) => props.theme.input.textSize};
    height: 40px;

    &:focus,
    &:active {
        outline: none;
        ${(props) => (props.isError ? 'border-color: #DE1C22;' : `border-color: #ddd`)}
    }

    ${(props) => (props.fullWidth ? 'width: 100%;' : '')}
    ${(props) => (props.readOnly ? 'background-color: transparent;' : '')}

    ${(props) => props.isError && 'border-color: #DE1C22;'}
    ${(props) => props.isError && 'color: #DE1C22;'}
`;

const SearchInputContainer = styled.div`
    position: relative;

    ${Input} {
        width: 100%;
        padding-left: 40px;
    }
`;

export const SearchInput = React.forwardRef(
    (
        props: InputProps &
            React.ComponentProps<'input'> & {
                onSearch?: (filter: string) => void;
                containerStyle?: CSSProperties;
            } & {
                rightElement?: React.ReactNode;
            },
        ref: ForwardedRef<HTMLInputElement>
    ) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (props.onChange) props.onChange(e);

            handleSearch(e.currentTarget.value);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const handleSearch = useCallback(
            debounce((filter: string) => {
                if (props.onSearch) props.onSearch(filter);
            }, 400),
            []
        );

        return (
            <SearchInputContainer style={props.containerStyle}>
                <SearchInputIcon>
                    <SearchIcon />
                </SearchInputIcon>
                <Input {...omit(props, ['onSearch', 'ref', 'rightElement', 'containerStyle'])} ref={ref} onChange={handleChange} />

                <SearchInputRightElementContainer>{props.rightElement}</SearchInputRightElementContainer>
            </SearchInputContainer>
        );
    }
);

const SearchInputRightElementContainer = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 10px;
    color: ${(props) => props.theme.app.primary};
`;

const SearchInputIcon = styled(Icon)`
    position: absolute;
    top: calc(50% - 12px);
    left: 10px;
    color: ${(props) => props.theme.app.primary};
`;

export const Command = styled.div`
    display: inline-block;
    padding: 3px 5px;
    border-radius: 3px;
    background-color: #eee;
    font-size: 10px;
`;

const CopyInputContainer = styled.div`
    position: relative;
    width: 100%;
    display: grid;
    grid-column-gap: 4px;
    grid-template-columns: 0.95fr 0.05fr;

    ${Icon} {
        position: absolute;
        top: calc(50% - 12px);
        right: 10px;
        color: ${theme.app.primary};
        cursor: pointer;
    }

    ${Input} {
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > .tooltip {
        visibility: visible;
        background-color: #555;
        color: #fff;
        text-align: center;
        border-radius: 6px;
        padding: 5px;
        position: absolute;
        z-index: 1;
        top: calc(50% - 12px);
        right: 44px;
        margin-left: -60px;
        opacity: 1;
        transition: opacity 0.3s;
    }
`;

export const CopyInput: FC<InputProps & ReactType<HTMLInputElement>> = (props: InputProps & ReactType<HTMLInputElement>) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipTimeout = useRef<any | undefined>();

    const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        const { value } = props;
        if (value) {
            if ('clipboard' in navigator) {
                return navigator.clipboard
                    .writeText(value as string)
                    .then(() => {
                        setShowTooltip(true);
                        if (tooltipTimeout.current) {
                            clearTimeout(tooltipTimeout.current);
                        }
                        tooltipTimeout.current = setTimeout(() => {
                            setShowTooltip(false);
                        }, 2000);
                    })
                    .catch(() => {
                        alert('Failed to copy text');
                    });
            } else {
                return document.execCommand('copy', true, value as string);
            }
        }
    };

    return (
        <CopyInputContainer>
            <Input readOnly={true} {...(props as InputProps)} />
            <Icon onClick={handleClick}>
                <CopyIcon />
            </Icon>
            {showTooltip && <div className='tooltip'>Copied</div>}
        </CopyInputContainer>
    );
};

export const _FileInputContainer = styled.div`
    display: flex;
    align-items: center;

    input {
        display: none;
    }

    span {
        margin-left: 10px;
    }
`;

type SingleFileInputProps = {
    multiple?: false;
    accept?: string;
    disabled?: boolean;
    fileName?: string;
    label?: React.ReactNode;
    onChange: (file: File | null) => void;
    ref?: any;
};
type MultipleFileInputProps = {
    multiple: true;
    accept?: string;
    disabled?: boolean;
    fileName?: string;
    label?: React.ReactNode;
    onChange: (files: FileList | null) => void;
    ref?: any;
};
export type FileInputProps = SingleFileInputProps | MultipleFileInputProps;

export const FileInput: FC<FileInputProps> = forwardRef<{ reset: () => void }, FileInputProps>(
    ({ multiple, disabled, accept, fileName, label, onChange }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null);

        const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.currentTarget.files;
            if (files && files.length > 0) {
                if (multiple) {
                    onChange(files);
                    return;
                }
                onChange(files[0]);
            } else {
                onChange(null);
            }
        };

        const reset = useCallback(() => {
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                reset: reset
            }),
            [reset]
        );

        return (
            <_FileInputContainer>
                <input type='file' multiple={multiple} ref={inputRef} onChange={handleOnChange} accept={accept} />
                <BasicButton disabled={disabled} onClick={() => inputRef.current?.click()}>
                    {label || T._('Choose file', 'Admin')}
                </BasicButton>
                {fileName && <span>{fileName}</span>}
            </_FileInputContainer>
        );
    }
);

export const FormDate = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    label {
        margin: 5px 0 5px 0 !important;
        font: ${theme.app.font};
    }

    input {
        width: ${!isMobile() ? '180px' : '100%'};
        padding: ${!isMobile() ? '0 16px' : '0 7px'};
        margin: ${!isMobile() ? '5px 0 5px 0 !important' : ''};
    }
`;

export const StyledDatePicker = styled.input`
    border: 1px solid;
    border-width: 1px;
    border-color: rgb(222, 222, 222);
    border-radius: 4px;
    width: auto;
    height: 40px;
    color: #455a64;
    font-size: 12px;
    font-family: ${theme.app.font};

    &:focus,
    &:active {
        outline: none;
        border-color: ${theme.app.primary};
    }
`;

type DateType = 'datetime-local' | 'date' | 'time';

export type DatePickerInputProps = {
    label?: string;
    value?: string;
    type?: DateType;
    onChange: (date: string) => void;
};

export const DatePickerInput: FC<DatePickerInputProps> = ({ label, value, type, onChange }) => {
    const [date, setDate] = useState<string>(value ?? '');
    const dateType = type ?? 'datetime-local';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDate(e.currentTarget.value);
        onChange(e.currentTarget.value);
    };

    return (
        <FormDate>
            {label && <label>{T._(`${label}`, 'Admin')}</label>}
            <StyledDatePicker type={dateType} value={date} onChange={handleChange}></StyledDatePicker>
        </FormDate>
    );
};

const PasswordInputContainer = styled.div`
    position: relative;

    ${Icon} {
        position: absolute;
        top: calc(50% - 12px);
        right: 10px;
        cursor: pointer;
        width: 20px;
        height: 20px;
    }
`;

export const PasswordInput: FC<InputProps & ReactType<HTMLInputElement>> = (props: InputProps & ReactType<HTMLInputElement>) => {
    const [showPassword, setShowPassword] = useState(false);
    const [type, setType] = useState('password');
    const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setShowPassword((prev) => {
            prev = !showPassword;
            setType(prev ? 'text' : 'password');
            return prev;
        });
    };

    return (
        <PasswordInputContainer>
            <Input type={type} {...(props as InputProps)} />
            <Icon onClick={handleClick}>{showPassword ? <LowVisionSolid /> : <EyeSolid />}</Icon>
        </PasswordInputContainer>
    );
};
